import React, { useState, createRef, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CardContainer from '../../components/card/card-container.jsx';
import LinkDownloadSpinner from '../../components/link/link-download-spinner.jsx';
import LabelCategory from '../../components/label/label-category.jsx';
import LabelNumber from '../../components/label/label-number.jsx';
import Api from '../../api/global.service';
import { useAuth } from '../../components/content/auth.jsx';
import env from '../../environment.js';
import NoPagination from '../../components/pagination/noPagination.jsx';
import SelectCiclo from '../../components/select/select-ciclo.jsx';
import SearchColumn from '../../components/search/search-column.jsx';
import ModalPadron from '../../components/modal/modal-padron.jsx';
import EditLink from '../../components/link/edit-link.jsx';
import ModalParcelas from '../../components/modal/modal-parcelas.jsx';
import ModalDescargaFichas from '../../components/modal/modal-descarga-fichas.jsx';
import ModalWhatsappConfirm from '../../components/modal/modal-whatsapp-confirm.jsx';
import ModalPadronCompletoDownload from '../../components/modal/modal-padron-completo-download.jsx';
import updateLocalData from '../../utils/updateLocalData.js';
import Swal from 'sweetalert2';
import { useCoopConfig } from '../../components/content/coop-config.jsx';
import { Colors } from '../../constants/Colors.js';
import useEtiquetas from '../../hooks/useEtiquetas';
import transformWord from '../../utils/transformWord';

const RELOAD_TIME = 15000; // 15segs

const PadronCompletoList = () => {
  const auth = useAuth();
  const { etiquetas } = useEtiquetas();
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [dataFormatos, setDataFormatos] = useState([]);
  const [ciclo, setCiclo] = useState({});
  const [blocking, setBlocking] = useState(true);
  const [blockingDownload, setBlockingDownload] = useState({ id_cuenta: null, status: false });
  const [blockingWhatsapp, setBlockingWhatsapp] = useState({ id_cuenta: null, status: false });
  const [whatsappSelected, setWhatsappSelected] = useState({
    id_cuenta: null,
    nombreProductor: null,
    telefono: null,
    productor_uuid: null,
    id_ciclo: null,
  });
  const [edit, setEdit] = useState(null);
  const [editNumber, setEditNumber] = useState(null);
  const [editDate, setEditDate] = useState(null);
  const [editName, setEditName] = useState(null);
  const [editSelect, setEditSelect] = useState(null);
  const [editSelectLocalidad, setEditSelectLocalidad] = useState(null);
  const [showParcelas, setShowParcelas] = useState(false);
  const [showDescarga, setShowDescarga] = useState(false);
  const [showDescargaExcel, setShowDescargaExcel] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [parcelasSelectedData, setParcelasSelectedData] = useState({
    id_cuenta: null,
    nombreProductor: null,
  });
  const [unitConversions, setUnitConversions] = useState({});
  const coopConfig = useCoopConfig();

  const dialog = createRef();
  const whatsappModalRef = useRef(null);
  const onClickEdit = (event, data) => {
    event.preventDefault();
    setEdit(data);
  };

  const onClickEditNumber = (event, data) => {
    event.preventDefault();
    setEditNumber(data);
  };

  const onClickEditDate = (event, data) => {
    event.preventDefault();
    setEditDate(data);
  };

  const onClickEditName = (event, data) => {
    event.preventDefault();
    setEditName(data);
  };

  const onClickEditSelect = (event, data) => {
    event.preventDefault();
    setEditSelect(data);
  };

  const onClickEditSelectLocalidad = (event, data) => {
    event.preventDefault();
    setEditSelectLocalidad(data);
  };

  useEffect(() => {
    if (edit !== null) {
      dialog.current.editText(edit);
    }
  }, [edit]);

  useEffect(() => {
    if (editNumber !== null) {
      editNumber.type == 'numberLabel' ? dialog.current.editNumberLabel(editNumber) : dialog.current.editNumber(editNumber);
    }
  }, [editNumber]);

  useEffect(() => {
    if (editName !== null) {
      dialog.current.editName(editName);
    }
  }, [editName]);

  useEffect(() => {
    if (editSelect !== null) {
      dialog.current.editSelect(editSelect);
    }
  }, [editSelect]);

  useEffect(() => {
    if (editSelectLocalidad !== null) {
      dialog.current.editSelectLocalidad(editSelectLocalidad);
    }
  }, [editSelectLocalidad]);

  useEffect(() => {
    if (editDate !== null) {
      dialog.current.editDate(editDate);
    }
  }, [editDate]);

  const isMielCoop = +coopConfig.config?.cooperativa?.producto?.id === env.productos.MIEL;
  const isCafeCoop = +coopConfig.config?.cooperativa?.producto?.id === env.productos.CAFE;
  const producto = coopConfig.config?.cooperativa?.producto?.nombre || 'café';
  const productoUpper = transformWord(producto, ['uppercase']);

  const colVisible = (key, defaultVisible) => coopConfig.columnaVisible('padron_completo', key, defaultVisible);

  const columns = React.useMemo(
    () => [
      {
        Header: 'INFORMACIÓN DE PRODUCTOR',
        columns: [
          {
            Header: 'CLAVE',
            accessor: 'productor_clave_tx',
            sortable: true,
            minWidth: 50,
            show: colVisible('productor_clave_tx', true),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'text',
                accessor: 'productor_clave_tx',
                oldValue: value,
                columnName: 'CLAVE',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                setData: newValue =>
                  updateLocalData({ productor_clave_tx: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink onClickEdit={e => onClickEdit(e, editData)} column="productor_clave_tx" updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
          {
            Header: 'CÓDIGO',
            accessor: 'productor_codigo_tx',
            sortable: true,
            minWidth: 50,
            show: colVisible('productor_codigo_tx', !!+coopConfig.config?.usa_codigo_tx),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'text',
                accessor: 'productor_codigo_tx',
                oldValue: value,
                columnName: 'CÓDIGO',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                setData: newValue =>
                  updateLocalData({ productor_codigo_tx: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink onClickEdit={e => onClickEdit(e, editData)} column="productor_codigo_tx" updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
          {
            Header: 'NOMBRE PRODUCTOR',
            accessor: 'productor_nombre',
            sortable: true,
            minWidth: 220,
            show: colVisible('productor_nombre', true),
            Cell: ({ row, value }) => {
              const editData = {
                ...row.original,
                type: 'name',
                accessor: 'productor_nombre',
                columnName: 'NOMBRE PRODUCTOR',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                nombres: row.original.nombres,
                apellido_paterno: row.original.apellido_paterno,
                apellido_materno: row.original.apellido_materno,
                setData: newValue =>
                  updateLocalData(
                    {
                      productor_nombre: newValue.fullname,
                      nombres: newValue.nombres,
                      apellido_paterno: newValue.apellido_paterno,
                      apellido_materno: newValue.apellido_materno,
                    },
                    setData,
                    row.index,
                    setOriginalData,
                    row.original.id_cuenta
                  ),
              };

              if (row.original.productor_uuid !== null) {
                const updatedClass =
                  Array.isArray(row.original.updated) && row.original.updated.includes('productor_nombre') ? 'updated' : '';
                return (
                  <div className={updatedClass} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Link
                      to={{ pathname: `/admin/cuentas/perfil/${row.original.productor_uuid}` }}
                      className="m-r-2"
                      style={{ color: '#789D16' }}>
                      <span>{value}</span>
                    </Link>
                    <div className="edit_hover_class" style={{ minWidth: '45px', paddingLeft: '15px' }}>
                      <EditLink onClickEdit={e => onClickEditName(e, editData)} />
                    </div>
                  </div>
                );
              } else {
                return (
                  <EditLink onClickEdit={e => onClickEditName(e, editData)} column="productor_nombre" updated={row.original.updated}>
                    <p style={{ margin: 0 }}>{value}</p>
                  </EditLink>
                );
              }
            },
          },
          {
            Header: 'LOCALIDAD',
            accessor: 'localidad',
            sortable: true,
            minWidth: 50,
            show: colVisible('localidad', true),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'select',
                accessor: 'localidad',
                id_localidad: row.original.id_localidad,
                id_municipio: row.original.id_municipio,
                columnName: 'LOCALIDAD / MPIO',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                setData: newValue =>
                  updateLocalData(
                    {
                      localidad: newValue.localidad,
                      id_localidad: newValue.id_localidad,
                      municipio: newValue.municipio,
                      id_municipio: newValue.id_municipio,
                    },
                    setData,
                    row.index,
                    setOriginalData,
                    row.original.id_cuenta
                  ),
              };
              return (
                <EditLink onClickEdit={e => onClickEditSelectLocalidad(e, editData)} column="localidad" updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
          {
            Header: 'MUNICIPIO',
            accessor: 'municipio',
            sortable: true,
            minWidth: 50,
            show: colVisible('municipio', true),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'select',
                accessor: 'localidad',
                id_localidad: row.original.id_localidad,
                id_municipio: row.original.id_municipio,
                columnName: 'LOCALIDAD / MPIO',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                setData: newValue =>
                  updateLocalData(
                    {
                      localidad: newValue.localidad,
                      id_localidad: newValue.id_localidad,
                      municipio: newValue.municipio,
                      id_municipio: newValue.id_municipio,
                    },
                    setData,
                    row.index,
                    setOriginalData,
                    row.original.id_cuenta
                  ),
              };
              return (
                <EditLink onClickEdit={e => onClickEditSelectLocalidad(e, editData)} column="municipio" updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
          {
            Header: 'REGIÓN',
            accessor: 'region',
            sortable: true,
            minWidth: 50,
            show: colVisible('region', !isMielCoop),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'select',
                accessor: 'region',
                oldValue: row.original.id_cuenta_region,
                columnName: 'REGIÓN',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_ciclo: row.original.id_ciclo,
                catalog: 'sic/regiones',
                setData: newValue =>
                  updateLocalData(
                    { region: newValue.label, id_cuenta_region: newValue.id },
                    setData,
                    row.index,
                    setOriginalData,
                    row.original.id_cuenta
                  ),
              };
              return (
                <EditLink onClickEdit={e => onClickEditSelect(e, editData)} column="region" updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
          {
            Header: 'DESCARGAR FICHAS',
            accessor: 'formatos_sic',
            sortable: false,
            minWidth: 50,
            show: colVisible('formatos_sic', true),
            Cell: ({ value, row }) => {
              return (
                <>
                  {blockingDownload.status && blockingDownload.id_cuenta === row.original.id_cuenta ? (
                    <LinkDownloadSpinner />
                  ) : (
                    <Link
                      to="#"
                      onClick={e => {
                        e.preventDefault();
                        handleDownloadFormatos(row.original);
                      }}
                      className="m-r-2"
                      style={{ color: Colors.primary_500 }}>
                      <img src={env.images.icon.pdf} className="pr-1" />
                      <span>Descargar</span>
                    </Link>
                  )}
                </>
              );
            },
          },
          {
            Header: 'ENVIAR POR WHATSAPP',
            accessor: 'whatsapp_sic',
            sortable: false,
            minWidth: 50,
            show: coopConfig.usaWhatsapp(),
            Cell: ({ row }) => {
              return (
                <>
                  {blockingWhatsapp.status && blockingWhatsapp.id_cuenta === row.original.id_cuenta ? (
                    <LinkDownloadSpinner />
                  ) : (
                    <Link
                      to="#"
                      onClick={e => {
                        e.preventDefault();
                        openWhatsappConfirm(row.original);
                      }}
                      className="m-r-2"
                      style={{ color: Colors.primary_500, display: 'inline-flex', alignItems: 'center' }}>
                      <i className="fab fa-whatsapp" style={{ fontSize: 18 }}></i>
                      <span className="pl-1">Enviar</span>
                    </Link>
                  )}
                </>
              );
            },
          },
          {
            Header: coopConfig.config?.ine_etiqueta ? coopConfig.config?.ine_etiqueta : 'INE',
            accessor: 'ine',
            sortable: true,
            minWidth: 190,
            show: colVisible('ine', true),
            Cell: ({ row, value }) => {
              const editData = {
                ...row.original,
                type: 'text',
                accessor: 'ine',
                oldValue: value,
                columnName: coopConfig.config?.ine_etiqueta ? coopConfig.config?.ine_etiqueta : 'INE',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue => updateLocalData({ ine: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink onClickEdit={e => onClickEdit(e, editData)} column="ine" updated={row.original.updated}>
                  {value}
                </EditLink>
              );
            },
          },
          {
            Header: 'CURP',
            accessor: 'curp',
            sortable: true,
            minWidth: 190,
            show: colVisible('curp', true),
            Cell: ({ row, value }) => {
              const editData = {
                ...row.original,
                type: 'text',
                accessor: 'curp',
                oldValue: value,
                columnName: 'CURP',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue => updateLocalData({ curp: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink onClickEdit={e => onClickEdit(e, editData)} column="curp" updated={row.original.updated}>
                  {value}
                </EditLink>
              );
            },
          },
          {
            Header: 'UPP',
            accessor: 'upp',
            sortable: true,
            minWidth: 190,
            show: colVisible('upp', isMielCoop),
            Cell: ({ row, value }) => {
              const editData = {
                ...row.original,
                type: 'text',
                accessor: 'upp',
                oldValue: value,
                columnName: 'UPP',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue => updateLocalData({ upp: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink onClickEdit={e => onClickEdit(e, editData)} column="upp" updated={row.original.updated}>
                  {value}
                </EditLink>
              );
            },
          },
          {
            Header: 'FECHA DE NACIMIENTO',
            accessor: 'productor_fecha_nacimiento',
            sortable: true,
            minWidth: 50,
            show: colVisible('productor_fecha_nacimiento', !isMielCoop),
            Cell: ({ row, value }) => {
              const editData = {
                ...row.original,
                type: 'date',
                accessor: 'productor_fecha_nacimiento',
                oldValue: value,
                columnName: 'FECHA DE NACIMIENTO',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData({ productor_fecha_nacimiento: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink
                  onClickEdit={e => onClickEditDate(e, editData)}
                  column="productor_fecha_nacimiento"
                  updated={row.original.updated}>
                  {value}
                </EditLink>
              );
            },
          },
          {
            Header: 'GÉNERO',
            accessor: 'productor_genero',
            sortable: true,
            minWidth: 190,
            show: colVisible('productor_genero', !isMielCoop),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'select',
                accessor: 'productor_genero',
                oldValue: row.original.productor_id_genero,
                columnName: 'GÉNERO',
                nombreProductor: row.original.productor_nombre,
                // nombreParcela: row.original.parcelas?.[4]?.nombre,
                id_cuenta: row.original.id_cuenta,
                catalog: 'admin/sic/catalog/generos',
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData(
                    { productor_genero: newValue.label, productor_genero_id: newValue.id },
                    setData,
                    row.index,
                    setOriginalData,
                    row.original.id_cuenta
                  ),
              };

              return (
                <EditLink onClickEdit={e => onClickEditSelect(e, editData)} column="productor_genero" updated={row.original.updated}>
                  {value}
                </EditLink>
              );
            },
          },
          {
            Header: 'NÚMERO DE CELULAR',
            accessor: 'contacto_celular',
            sortable: true,
            minWidth: 50,
            show: colVisible('contacto_celular', !isMielCoop),
            Cell: ({ row, value }) => {
              const editData = {
                ...row.original,
                type: 'text',
                accessor: 'contacto_celular',
                oldValue: value,
                columnName: 'NÚMERO DE CELULAR',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                max: 25,
                setData: newValue =>
                  updateLocalData({ contacto_celular: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink onClickEdit={e => onClickEdit(e, editData)} column="contacto_celular" updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
          {
            Header: 'CORREO ELECTRÓNICO',
            accessor: 'contacto_correo',
            sortable: true,
            minWidth: 50,
            show: colVisible('contacto_correo', !isMielCoop),
            Cell: ({ row, value }) => {
              const editData = {
                ...row.original,
                type: 'text',
                accessor: 'contacto_correo',
                oldValue: value,
                columnName: 'CORREO ELECTRÓNICO',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData({ contacto_correo: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink onClickEdit={e => onClickEdit(e, editData)} column="contacto_correo" updated={row.original.updated}>
                  {value}
                </EditLink>
              );
            },
          },
          {
            Header: 'CATEGORÍA CICLO ACTUAL',
            accessor: 'cuenta_categoria',
            sortable: true,
            minWidth: 90,
            show: colVisible('cuenta_categoria', true),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'select',
                accessor: 'cuenta_categoria',
                oldValue: row.original.id_cuenta_categoria,
                columnName: 'CATEGORÍA CICLO ACTUAL',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_ciclo: row.original.id_ciclo,
                catalog: 'categorias',
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData(
                    {
                      cuenta_categoria: newValue.label,
                      id_cuenta_categoria: newValue.id,
                      cuenta_categoria_color: '',
                    },
                    setData,
                    row.index,
                    setOriginalData,
                    row.original.id_cuenta
                  ),
              };
              if (value !== null) {
                return (
                  <EditLink onClickEdit={e => onClickEditSelect(e, editData)} column="cuenta_categoria" updated={row.original.updated}>
                    <LabelCategory label={value} color={row.original.cuenta_categoria_color} />
                  </EditLink>
                );
              }
              return (
                <EditLink onClickEdit={e => onClickEditSelect(e, editData)} column="cuenta_categoria" updated={row.original.updated}>
                  {value}
                </EditLink>
              );
            },
          },
          {
            Header: 'CATEGORÍA CICLO PASADO',
            accessor: 'categoria_anterior',
            sortable: true,
            minWidth: 50,
            show: colVisible('categoria_anterior', true),
            Cell: ({ row }) => {
              if (row.original.cuenta_categoria_anterior !== null && row.original.cuenta_categoria_anterior !== undefined) {
                const categoriaAnterior = row.original.cuenta_categoria_anterior;
                const colorCategoriaAnterior = row.original.cuenta_categoria_color_anterior;
                return <LabelCategory label={categoriaAnterior} color={colorCategoriaAnterior} />;
              }
              return null;
            },
          },
          {
            Header: 'FECHA INGRESO',
            accessor: 'productor_fecha_ingreso',
            sortable: true,
            minWidth: 50,
            show: colVisible('productor_fecha_ingreso', true),
            Cell: ({ row, value }) => {
              const editData = {
                ...row.original,
                type: 'date',
                accessor: 'productor_fecha_ingreso',
                oldValue: value,
                columnName: 'FECHA INGRESO',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData({ productor_fecha_ingreso: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink onClickEdit={e => onClickEditDate(e, editData)} column="productor_fecha_ingreso" updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
          {
            Header: 'FECHA INGRESO TRANSICIÓN',
            accessor: 'productor_fecha_transicion',
            sortable: true,
            minWidth: 50,
            show: colVisible('productor_fecha_transicion', true),
            Cell: ({ row, value }) => {
              const editData = {
                ...row.original,
                type: 'date',
                accessor: 'productor_fecha_transicion',
                oldValue: value,
                columnName: 'FECHA INGRESO TRANSICIÓN',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData({ productor_fecha_transicion: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink
                  onClickEdit={e => onClickEditDate(e, editData)}
                  column="productor_fecha_transicion"
                  updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
        ],
      },
      {
        Header: 'INFORMACIÓN PROGRAMA ORGÁNICO',
        columns: [
          {
            Header: `${transformWord(etiquetas?.parcela, ['plural', 'uppercase'])}`,
            accessor: 'cuenta_parcelas',
            sortable: true,
            minWidth: 70,
            show: colVisible('cuenta_parcelas', true),
            Cell: ({ value, row }) => {
              return (
                <button
                  type="button"
                  className="btn btn-sm btn-sirio"
                  style={{ borderRadius: '10px 10px 10px 10px' }}
                  onClick={() => {
                    setShowParcelas(true);
                    setParcelasSelectedData({
                      id_cuenta: row.original.id_cuenta,
                      nombreProductor: row.original.productor_nombre,
                    });
                    setSelectedRow(row.index);
                  }}>
                  <i className="fa fa-list"></i> {value} {transformWord(etiquetas?.parcela, ['plural', 'capitalize'])}
                </button>
              );
            },
          },
          {
            Header: 'FECHA APLICACIÓN PROD. PROHIBIDOS',
            accessor: 's3_cafeorganico_otrans_fechaprohibidos',
            sortable: true,
            minWidth: 50,
            show: colVisible('s3_cafeorganico_otrans_fechaprohibidos', true),
            Cell: ({ row, value }) => {
              const editData = {
                ...row.original,
                type: 'date',
                accessor: 's3_cafeorganico_otrans_fechaprohibidos',
                oldValue: value,
                columnName: 'FECHA APLICACIÓN PROD. PROHIBIDOS',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData(
                    { s3_cafeorganico_otrans_fechaprohibidos: newValue },
                    setData,
                    row.index,
                    setOriginalData,
                    row.original.id_cuenta
                  ),
              };
              return (
                <EditLink
                  onClickEdit={e => onClickEditDate(e, editData)}
                  column="s3_cafeorganico_otrans_fechaprohibidos"
                  updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
          {
            Header: 'PROD. PROHIBIDOS UTILIZADOS',
            accessor: 's3_cafeorganico_otrans_insumosprohibidos',
            sortable: true,
            minWidth: 50,
            show: colVisible('s3_cafeorganico_otrans_insumosprohibidos', true),
            Cell: ({ row, value }) => {
              const editData = {
                ...row.original,
                type: 'text',
                accessor: 's3_cafeorganico_otrans_insumosprohibidos',
                oldValue: value,
                columnName: 'PROD. PROHIBIDOS UTILIZADOS',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData(
                    { s3_cafeorganico_otrans_insumosprohibidos: newValue },
                    setData,
                    row.index,
                    setOriginalData,
                    row.original.id_cuenta
                  ),
              };
              return (
                <EditLink
                  onClickEdit={e => onClickEdit(e, editData)}
                  column="s3_cafeorganico_otrans_insumosprohibidos"
                  updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
          {
            Header: 'NIVEL DE RIESGO',
            accessor: 'nivel_riesgo',
            sortable: true,
            minWidth: 50,
            show: colVisible('nivel_riesgo', true),
            Cell: ({ row, value }) => {
              if (!value) {
                return '';
              }
              const editData = {
                type: 'select',
                accessor: 'nivel_riesgo',
                oldValue: row.original.nivel_riesgo_id,
                columnName: 'NIVEL DE RIESGO',
                nombreProductor: row.original.productor_nombre,
                // nombreParcela: row.original.parcelas?.[4]?.nombre,
                id_cuenta: row.original.id_cuenta,
                catalog: 'admin/sic/catalog/niveles_riesgo',
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData(
                    { nivel_riesgo: newValue.label, nivel_riesgo_id: newValue.id },
                    setData,
                    row.index,
                    setOriginalData,
                    row.original.id_cuenta
                  ),
              };
              return (
                <EditLink onClickEdit={e => onClickEditSelect(e, editData)} column="nivel_riesgo" updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
          {
            Header: 'EVALUACIÓN COMITÉ',
            accessor: 'condicion_str',
            sortable: true,
            minWidth: 50,
            show: colVisible('condicion_str', true),
          },
          {
            Header: 'CONDICIONES DE EVALUACIÓN',
            accessor: 'condicion_detalle',
            sortable: true,
            minWidth: 290,
            show: colVisible('condicion_detalle', true),
          },
          {
            Header: 'FECHA PARA ESTABLECER LA ACCIÓN CORRECTIVA',
            accessor: 'plazo_accion_correctiva',
            sortable: true,
            minWidth: 120,
            show: colVisible('plazo_accion_correctiva', isCafeCoop),
          },
          {
            Header: 'SUPERFICIE TOTAL DE TERRENO (ha)',
            accessor: 'calculada',
            show: colVisible('calculada', true),
            Cell: ({ row }) => {
              let c = row.original;

              const t =
                parseFloat(c.cuenta_superficie_total) +
                parseFloat(c.s2_autoconsumo_superficie) +
                parseFloat(c.s2_otrocultivo_superficie) +
                parseFloat(c.s2_potrero_superficie) +
                parseFloat(c.s2_bosque_superficie) +
                parseFloat(c.s2_acahual_superficie) +
                parseFloat(c.s2_reserva_superficie);

              const updatedClass =
                Array.isArray(row.original.updated) && row.original.updated.includes('cuenta_superficie_total') ? 'updated' : '';
              return (
                <div className={updatedClass}>
                  <LabelNumber value={t} digits={2} />
                </div>
              );
            },
            sortable: true,
            minWidth: 50,
          },

          {
            Header: `SUPERFICIE ${productoUpper} ORGÁNIC${isMielCoop ? 'A' : 'O'} (ha)`,
            accessor: 'cuenta_superficie_total',
            sortable: true,
            minWidth: 50,
            show: colVisible('cuenta_superficie_total', true),
            Cell: ({ value, row }) => {
              const updatedClass =
                Array.isArray(row.original.updated) && row.original.updated.includes('cuenta_superficie_total') ? 'updated' : '';
              return (
                <div className={updatedClass}>
                  <LabelNumber value={value} digits={2} />
                </div>
              );
            },
          },
          {
            Header: 'TIPO DE TENENCIA',
            accessor: 'tenencia_tierra',
            sortable: true,
            minWidth: 50,
            show: colVisible('tenencia_tierra', !isMielCoop),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'select',
                accessor: 'tenencia_tierra',
                oldValue: row.original.tenencia_tierra_id,
                columnName: 'TIPO DE TENENCIA',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                catalog: 'admin/sic/catalog/tenencia',
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData(
                    { tenencia_tierra: newValue.label, tenencia_tierra_id: newValue.id },
                    setData,
                    row.index,
                    setOriginalData,
                    row.original.id_cuenta
                  ),
              };
              return (
                <EditLink onClickEdit={e => onClickEditSelect(e, editData)} column="tenencia_tierra" updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
          {
            Header: 'DOCUMENTO DE PROPIEDAD',
            accessor: 'tenencia_papeles_sino',
            sortable: true,
            minWidth: 50,
            show: colVisible('tenencia_papeles_sino', !isMielCoop),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'select',
                accessor: 'tenencia_papeles_sino',
                oldValue: row.original.tenencia_papeles_sino_id,
                columnName: 'DOCUMENTO DE PROPIEDAD',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                catalog: 'admin/sic/catalog/si_no',
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData(
                    { tenencia_papeles_sino: newValue.label, tenencia_papeles_sino_id: newValue.id },
                    setData,
                    row.index,
                    setOriginalData,
                    row.original.id_cuenta
                  ),
              };
              return (
                <EditLink onClickEdit={e => onClickEditSelect(e, editData)} column="tenencia_papeles_sino" updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
          {
            Header: 'PRODUCTOS',
            accessor: 'cultivos_activos',
            sortable: true,
            minWidth: 50,
            width: 150,
            show: colVisible('cultivos_activos', !!+coopConfig.config?.usa_formato_apodip),
          },
          {
            Header: 'ESTIMACIÓN DE COSECHA PIMIENTA GORDA ' + unitConversions?.estimacion_cosecha_label,
            accessor: 'sum_actual_pimientanegra',
            sortable: true,
            minWidth: 50,
            width: 150,
            show: colVisible('sum_actual_pimientanegra', auth.user.id_cooperativa == 56),
            Cell: ({ value, row }) => {
              const updatedClass =
                Array.isArray(row.original.updated) && row.original.updated.includes('sum_actual_pimientanegra') ? 'updated' : '';
              return (
                <div className={updatedClass}>
                  <LabelNumber value={value} digits={2} />
                </div>
              );
            },
          },
          {
            Header: 'ESTIMACIÓN DE COSECHA CACAO ' + unitConversions?.estimacion_cosecha_label,
            accessor: 'sum_actual_cacao',
            sortable: true,
            minWidth: 50,
            width: 150,
            show: colVisible('sum_actual_cacao', auth.user.id_cooperativa == 36),
            Cell: ({ value, row }) => {
              const updatedClass =
                Array.isArray(row.original.updated) && row.original.updated.includes('max_actual_cacao') ? 'updated' : '';
              return (
                <div className={updatedClass}>
                  <LabelNumber value={value} digits={2} />
                </div>
              );
            },
          },
          {
            Header: 'ESTIMACIÓN DE COSECHA MANI ' + unitConversions?.estimacion_cosecha_label,
            accessor: 'sum_actual_mani',
            sortable: true,
            minWidth: 50,
            width: 150,
            show: colVisible('sum_actual_mani', auth.user.id_cooperativa == 36),
            Cell: ({ value, row }) => {
              const updatedClass = Array.isArray(row.original.updated) && row.original.updated.includes('max_actual_mani') ? 'updated' : '';
              return (
                <div className={updatedClass}>
                  <LabelNumber value={value} digits={2} />
                </div>
              );
            },
          },
          {
            Header: 'ESTIMACIÓN DE COSECHA CARDAMOMO ' + unitConversions?.estimacion_cosecha_label,
            accessor: 'sum_actual_cardamomo',
            sortable: true,
            minWidth: 50,
            width: 150,
            show: colVisible('sum_actual_cardamomo', auth.user.id_cooperativa == 36),
            Cell: ({ value, row }) => {
              const updatedClass =
                Array.isArray(row.original.updated) && row.original.updated.includes('max_actual_cardamomo') ? 'updated' : '';
              return (
                <div className={updatedClass}>
                  <LabelNumber value={value} digits={2} />
                </div>
              );
            },
          },
          {
            Header: `ESTIMACIÓN DE COSECHA TOTAL ${productoUpper} ` + unitConversions?.estimacion_cosecha_label,
            accessor: 'tx_estimacion_cosecha_inspector_or_total',
            sortable: true,
            minWidth: 50,
            width: 150,
            Cell: ({ value, row }) => {
              const updatedClass =
                Array.isArray(row.original.updated) && row.original.updated.includes('tx_estimacion_cosecha_inspector_or_total')
                  ? 'updated'
                  : '';
              return (
                <div className={updatedClass}>
                  <LabelNumber value={value} digits={2} />
                </div>
              );
            },
          },
          {
            Header: `ESTIMACIÓN DE COSECHA TOTAL ${productoUpper} KGS`,
            accessor: 'tx_estimacion_cosecha_inspector_total',
            sortable: true,
            minWidth: 50,
            width: 150,
            show: colVisible('tx_estimacion_cosecha_inspector_total', auth.user.id_cooperativa != 3),
            Cell: ({ value, row }) => {
              const updatedClass =
                Array.isArray(row.original.updated) && row.original.updated.includes('tx_estimacion_cosecha_inspector_total')
                  ? 'updated'
                  : '';
              return (
                <div className={updatedClass}>
                  <LabelNumber value={value} digits={2} />
                </div>
              );
            },
          },
          {
            Header: `ESTIMACIÓN DE ENTREGA ${productoUpper} (PRODUCTOR) ` + unitConversions?.estimacion_cosecha_label,
            accessor: 'tx_estimacion_cosecha_productor_or_total',
            sortable: true,
            minWidth: 50,
            show: colVisible('tx_estimacion_cosecha_productor_or_total', !isMielCoop),
            Cell: ({ value, row }) => {
              const updatedClass =
                Array.isArray(row.original.updated) && row.original.updated.includes('tx_estimacion_cosecha_productor_or_total')
                  ? 'updated'
                  : '';
              return (
                <div className={updatedClass}>
                  <LabelNumber value={value} digits={2} />
                </div>
              );
            },
          },
          {
            Header: `ESTIMACIÓN DE ENTREGA ${productoUpper} (PRODUCTOR) KGS`,
            accessor: 'tx_estimacion_cosecha_productor_total',
            sortable: true,
            minWidth: 50,
            show: colVisible('tx_estimacion_cosecha_productor_total', !isMielCoop),
            Cell: ({ value, row }) => {
              const updatedClass =
                Array.isArray(row.original.updated) && row.original.updated.includes('tx_estimacion_cosecha_productor_total')
                  ? 'updated'
                  : '';
              return (
                <div className={updatedClass}>
                  <LabelNumber value={value} digits={2} />
                </div>
              );
            },
          },
          {
            Header: `# ${transformWord(etiquetas?.planta, ['plural', 'uppercase'])} TOTALES ${productoUpper}`,
            accessor: 'tx_no_plantas_totales',
            sortable: true,
            minWidth: 50,
            show: colVisible('tx_no_plantas_totales', true),
            Cell: ({ value, row }) => {
              const updatedClass =
                Array.isArray(row.original.updated) && row.original.updated.includes('tx_no_plantas_totales') ? 'updated' : '';
              return (
                <div className={updatedClass}>
                  <LabelNumber value={value} />
                </div>
              );
            },
          },
          {
            Header: `# ${transformWord(etiquetas?.planta, ['plural', 'uppercase'])} PRODUCTIVAS TOTALES`,
            accessor: 'plantas_productivas_totales',
            sortable: true,
            minWidth: 50,
            show: colVisible('plantas_productivas_totales', auth.user.id_cooperativa != 17 && !isMielCoop),
            Cell: ({ value }) => {
              return <LabelNumber value={value}></LabelNumber>;
            },
          },
          {
            Header: `# ${transformWord(etiquetas?.planta, ['plural', 'uppercase'])} IMPRODUCTIVAS TOTALES`,
            accessor: 'plantas_improductivas_totales',
            sortable: true,
            minWidth: 50,
            show: colVisible('plantas_improductivas_totales', auth.user.id_cooperativa != 17 && !isMielCoop),
            Cell: ({ value }) => {
              return <LabelNumber value={value}></LabelNumber>;
            },
          },
          {
            Header: 'FECHA INSPECCIÓN INTERNA',
            accessor: 's3_inspeccion_fecha',
            sortable: true,
            minWidth: 50,
            show: colVisible('s3_inspeccion_fecha', true),
            Cell: ({ row, value }) => {
              const editData = {
                ...row.original,
                type: 'date',
                accessor: 's3_inspeccion_fecha',
                oldValue: value,
                columnName: 'FECHA INSPECCIÓN INTERNA',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData({ s3_inspeccion_fecha: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink onClickEdit={e => onClickEditDate(e, editData)} column="s3_inspeccion_fecha" updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
          {
            Header: 'NOMBRE INSPECTOR INTERNO',
            accessor: 's1_lista_inspector_str',
            sortable: true,
            minWidth: 50,
            show: colVisible('s1_lista_inspector_str', true),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'select',
                accessor: 's1_lista_inspector_str',
                oldValue: row.original.s1_lista_inspector,
                columnName: 'NOMBRE INSPECTOR INTERNO',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                catalog: 'admin/sic/inspectores',
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData(
                    { s1_lista_inspector_str: newValue.label, s1_lista_inspector: newValue.id },
                    setData,
                    row.index,
                    setOriginalData,
                    row.original.id_cuenta
                  ),
              };
              return (
                <EditLink onClickEdit={e => onClickEditSelect(e, editData)} column="s1_lista_inspector_str" updated={row.original.updated}>
                  <p style={{ margin: 0 }}>{value}</p>
                </EditLink>
              );
            },
          },
        ].filter(column => {
          // hide columns - replacing the "show" property due not working in some cases
          if (column.accessor === 'tx_estimacion_cosecha_inspector_or_total') {
            return (
              colVisible('tx_estimacion_cosecha_inspector_or_total', auth.user.id_cooperativa != 3) &&
              unitConversions?.estimacion_cosecha_label &&
              unitConversions?.estimacion_cosecha_label !== 'KGS'
            );
          } else if (column.accessor === 'tx_estimacion_cosecha_productor_or_total') {
            return unitConversions?.estimacion_cosecha_label && unitConversions?.estimacion_cosecha_label !== 'KGS';
          }
          return true;
        }),
      },
      {
        Header: `SUPERFICIE CULTIVOS NO ${productoUpper} (ha)`,
        columns: [
          {
            Header: 'AUTOCONSUMO',
            accessor: 's2_autoconsumo_superficie',
            sortable: true,
            minWidth: 50,
            show: colVisible('s2_autoconsumo_superficie', true),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'number',
                accessor: 's2_autoconsumo_superficie',
                oldValue: value,
                columnName: 'AUTOCONSUMO',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData({ s2_autoconsumo_superficie: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink
                  onClickEdit={e => onClickEditNumber(e, editData)}
                  column="s2_autoconsumo_superficie"
                  updated={row.original.updated}>
                  <LabelNumber value={value} digits={2}></LabelNumber>
                </EditLink>
              );
            },
          },
          {
            Header: 'OTROS CULTIVOS',
            accessor: 's2_otrocultivo_superficie',
            sortable: true,
            minWidth: 50,
            show: colVisible('s2_otrocultivo_superficie', true),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'number',
                accessor: 's2_otrocultivo_superficie',
                oldValue: value,
                columnName: 'OTROS CULTIVOS',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData({ s2_otrocultivo_superficie: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink
                  onClickEdit={e => onClickEditNumber(e, editData)}
                  column="s2_otrocultivo_superficie"
                  updated={row.original.updated}>
                  <LabelNumber value={value} digits={2}></LabelNumber>
                </EditLink>
              );
            },
          },
          {
            Header: 'POTRERO',
            accessor: 's2_potrero_superficie',
            sortable: true,
            minWidth: 50,
            show: colVisible('s2_potrero_superficie', true),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'number',
                accessor: 's2_potrero_superficie',
                oldValue: value,
                columnName: 'POTRERO',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData({ s2_potrero_superficie: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink onClickEdit={e => onClickEditNumber(e, editData)} column="s2_potrero_superficie" updated={row.original.updated}>
                  <LabelNumber value={value} digits={2}></LabelNumber>
                </EditLink>
              );
            },
          },
          {
            Header: 'BOSQUE',
            accessor: 's2_bosque_superficie',
            sortable: true,
            minWidth: 50,
            show: colVisible('s2_bosque_superficie', true),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'number',
                accessor: 's2_bosque_superficie',
                oldValue: value,
                columnName: 'BOSQUE',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData({ s2_bosque_superficie: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink onClickEdit={e => onClickEditNumber(e, editData)} column="s2_bosque_superficie" updated={row.original.updated}>
                  <LabelNumber value={value} digits={2}></LabelNumber>
                </EditLink>
              );
            },
          },
          {
            Header: 'ACAHUAL',
            accessor: 's2_acahual_superficie',
            sortable: true,
            minWidth: 50,
            show: colVisible('s2_acahual_superficie', true),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'number',
                accessor: 's2_acahual_superficie',
                oldValue: value,
                columnName: 'ACAHUAL',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData({ s2_acahual_superficie: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink onClickEdit={e => onClickEditNumber(e, editData)} column="s2_acahual_superficie" updated={row.original.updated}>
                  <LabelNumber value={value} digits={2}></LabelNumber>
                </EditLink>
              );
            },
          },
          {
            Header: 'RESERVA',
            accessor: 's2_reserva_superficie',
            sortable: true,
            minWidth: 50,
            show: colVisible('s2_reserva_superficie', !isMielCoop),
            Cell: ({ row, value }) => {
              const editData = {
                type: 'number',
                accessor: 's2_reserva_superficie',
                oldValue: value,
                columnName: 'RESERVA',
                nombreProductor: row.original.productor_nombre,
                id_cuenta: row.original.id_cuenta,
                id_sic_informacion_general: row.original.id_sic_informacion_general,
                setData: newValue =>
                  updateLocalData({ s2_reserva_superficie: newValue }, setData, row.index, setOriginalData, row.original.id_cuenta),
              };
              return (
                <EditLink onClickEdit={e => onClickEditNumber(e, editData)} column="s2_reserva_superficie" updated={row.original.updated}>
                  <LabelNumber value={value} digits={2}></LabelNumber>
                </EditLink>
              );
            },
          },
        ],
      },
    ],
    [unitConversions, dataFormatos, blockingDownload, blockingWhatsapp, etiquetas, isMielCoop, isCafeCoop, productoUpper, coopConfig.config]
  );

  const searchColumns = React.useMemo(
    () => [
      { value: 0, target: '', label: 'TODO' },
      { value: 1, target: 'productor_clave_tx', label: 'CLAVE' },
      { value: 2, target: 'productor_codigo_tx', label: 'CÓDIGO' },
      { value: 3, target: 'localidad', label: 'LOCALIDAD' },
      { value: 4, target: 'municipio', label: 'MUNICIPIO' },
      ...(colVisible('region', !isMielCoop) ? [{ value: 5, target: 'region', label: 'REGIÓN' }] : []),
      { value: 6, target: 'productor_nombre', label: 'NOMBRE PRODUCTOR' },
      { value: 7, target: 'curp', label: 'CURP' },
      ...(colVisible('upp', isMielCoop) ? [{ value: 8, target: 'upp', label: 'UPP' }] : []),
      ...(colVisible('contacto_celular', !isMielCoop) ? [{ value: 9, target: 'contacto_celular', label: 'NÚMERO DE CELULAR' }] : []),
      ...(colVisible('contacto_correo', !isMielCoop) ? [{ value: 10, target: 'contacto_correo', label: 'CORREO ELECTRÓNICO' }] : []),
      { value: 11, target: 'cuenta_categoria', label: 'CATEGORÍA CICLO ACTUAL' },
      ...(colVisible('categoria_anterior', true) ? [{ value: 12, target: 'categoria_anterior', label: 'CATEGORÍA CICLO PASADO' }] : []),
      { value: 13, target: 'productor_fecha_ingreso', label: 'FECHA INGRESO' },
      ...(colVisible('productor_fecha_transicion', true)
        ? [{ value: 14, target: 'productor_fecha_transicion', label: 'FECHA INGRESO TRANSICIÓN' }]
        : []),
      { value: 15, target: 'cuenta_parcelas', label: `NUMERO DE ${transformWord(etiquetas?.parcela, ['plural', 'uppercase'])}` },
      { value: 16, target: 's3_cafeorganico_otrans_fechaprohibidos', label: 'FECHA APLICACIÓN PROD. PROHIBIDOS' },
      { value: 17, target: 's3_cafeorganico_otrans_insumosprohibidos', label: 'PROD. PROHIBIDOS UTILIZADOS' },
      { value: 18, target: 'nivel_riesgo', label: 'NIVEL DE RIESGO' },
      { value: 19, target: 'condicion_str', label: 'EVALUACIÓN COMITÉ' },
      { value: 20, target: 'condicion_detalle', label: 'CONDICIONES DE EVALUACIÓN' },
      ...(colVisible('plazo_accion_correctiva', isCafeCoop)
        ? [{ value: 38, target: 'plazo_accion_correctiva', label: 'FECHA PARA ESTABLECER LA ACCIÓN CORRECTIVA' }]
        : []),
      { value: 21, target: 'calculada', label: 'SUPERFICIE TOTAL DE TERRENO (ha)' },
      { value: 22, target: 'cuenta_superficie_total', label: `SUPERFICIE ${productoUpper} ORGÁNICO (ha)` },
      ...(colVisible('tenencia_tierra', !isMielCoop) ? [{ value: 23, target: 'tenencia_tierra', label: 'TIPO DE TENENCIA' }] : []),
      ...(colVisible('tenencia_papeles_sino', !isMielCoop)
        ? [{ value: 24, target: 'tenencia_papeles_sino', label: 'DOCUMENTO DE PROPIEDAD' }]
        : []),
      {
        value: 25,
        target: 'tx_estimacion_cosecha_inspector_total',
        label: 'ESTIMACIÓN DE COSECHA TOTAL ' + unitConversions?.estimacion_cosecha_label,
      },
      ...(colVisible('tx_estimacion_cosecha_productor_total', !isMielCoop)
        ? [
            {
              value: 26,
              target: 'tx_estimacion_cosecha_productor_total',
              label: 'ESTIMACIÓN DE ENTREGA (PRODUCTOR) ' + unitConversions?.estimacion_cosecha_label,
            },
          ]
        : []),
      {
        value: 27,
        target: 'tx_no_plantas_totales',
        label: `# ${transformWord(etiquetas?.planta, ['plural', 'uppercase'])} TOTALES ${productoUpper}`,
      },
      ...(colVisible('plantas_productivas_totales', auth.user.id_cooperativa != 17 && !isMielCoop)
        ? [
            {
              value: 28,
              target: 'plantas_productivas_totales',
              label: `# ${transformWord(etiquetas?.planta, ['plural', 'uppercase'])} PRODUCTIVAS TOTALES`,
            },
          ]
        : []),
      ...(colVisible('plantas_improductivas_totales', auth.user.id_cooperativa != 17 && !isMielCoop)
        ? [
            {
              value: 29,
              target: 'plantas_improductivas_totales',
              label: `# ${transformWord(etiquetas?.planta, ['plural', 'uppercase'])} IMPRODUCTIVAS TOTALES`,
            },
          ]
        : []),
      { value: 30, target: 's3_inspeccion_fecha', label: 'FECHA INSPECCIÓN INTERNA' },
      { value: 31, target: 's1_lista_inspector_str', label: 'NOMBRE INSPECTOR INTERNO' },
      { value: 32, target: 's2_autoconsumo_superficie', label: 'AUTOCONSUMO' },
      { value: 33, target: 's2_otrocultivo_superficie', label: 'OTROS CULTIVOS' },
      { value: 34, target: 's2_potrero_superficie', label: 'POTRERO' },
      { value: 35, target: 's2_bosque_superficie', label: 'BOSQUE' },
      { value: 35, target: 's2_acahual_superficie', label: 'ACAHUAL' },
      ...(colVisible('s2_reserva_superficie', !isMielCoop) ? [{ value: 37, target: 's2_reserva_superficie', label: 'RESERVA' }] : []),
    ],
    [auth, etiquetas, isMielCoop, productoUpper, coopConfig.config]
  );

  const unitConversionsLabels = data => {
    if (data) {
      const d = data[0]; // applied to headers for all columns
      setUnitConversions({
        estimacion_cosecha_label: d?.estimacion_cosecha_label ? d.estimacion_cosecha_label.toUpperCase() : 'KGS',
      });
    }
  };

  const setFilter = value => {
    const cicloItem = value.target;
    setCiclo(cicloItem);
    setBlocking(true);
    Api.search('tx_padron_completo', {
      filter: [{ column: 'id_ciclo', operator: '=', value: cicloItem.id }],
    })
      .then(response => {
        const dataResponse = response.data;
        setData(dataResponse);
        setOriginalData(dataResponse);
        unitConversionsLabels(dataResponse);
        // aditional requests
        Api.search('tx_formatos', { filter: [{ column: 'id_ciclo', operator: '=', value: cicloItem.id }] })
          .then(resFormatos => {
            resFormatos.status == 200 ? setDataFormatos(resFormatos.data) : setDataFormatos([]);
          })
          .catch(e => {
            // console.log('error:', e);
          });
        setBlocking(false);
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        // console.log('error:', e);
        setBlocking(false);
      });
  };

  const setDataFilter = data => {
    setData(
      data.map(it => {
        return { ...it, updated: null };
      })
    );
  };

  /**
   * Get Headers background colors
   * alternate first row colors,
   * then alternate second row columns for parcelas only, ie after row 3
   * @param {number} row row number
   * @param {number} column column number
   * @returns {string} color
   */
  const getBackgroundColor = (row, column) => {
    if (row === 0) {
      if (column % 2 === 0) return '#5F5F59';
      return '#91918A';
    }
    if (row === 1) {
      // TODO: check this change: it renders the header with the same color in any case
      if (column > 2 && column % 2 === 0) return 'rgb(247, 239, 231)';
      return 'rgb(247, 239, 231)';
    }
    return 'rgb(247, 239, 231)';
  };

  /**
   *  Get Header font colors, first row white, rest dark
   * @param {number} row row number
   * @returns {string} color
   */
  const getColor = row => (row === 0 ? 'white' : 'rgb(95, 95, 89)');

  const closeModalParcelas = () => {
    setShowParcelas(false);
    setParcelasSelectedData({
      id_cuenta: null,
      nombreProductor: null,
    });
  };

  const handleDownloadFormatos = async row => {
    const { id_cuenta, productor_uuid, id_ciclo } = row;
    const formatos = dataFormatos.filter(df => df.id_cuenta == id_cuenta);
    if (formatos.length > 0) {
      let url;
      for (let i = 0; i < formatos.length; i++) {
        url = `pdf/${formatos[i].reporte}/${formatos[i].uuid}`;
        setBlockingDownload({ id_cuenta: id_cuenta, status: true });
        await downloadBlob(url);
        setBlockingDownload({ id_cuenta: id_cuenta, status: false });
      }
    }

    setBlockingDownload({ id_cuenta: id_cuenta, status: true });
    await downloadBlob(`comite_evaluaciones/${productor_uuid}/${id_ciclo}/pdf`);
    setBlockingDownload({ id_cuenta: id_cuenta, status: false });
  };

  const openWhatsappConfirm = row => {
    setWhatsappSelected({
      id_cuenta: row.id_cuenta,
      nombreProductor: row.productor_nombre,
      telefono: row.contacto_celular,
      productor_uuid: row.productor_uuid,
      id_ciclo: row.id_ciclo,
    });
    whatsappModalRef.current.open();
  };

  const handleSendWhatsapp = async () => {
    const { id_cuenta, productor_uuid, id_ciclo } = whatsappSelected;
    const formatos = dataFormatos.filter(df => df.id_cuenta == id_cuenta);
    whatsappModalRef.current.setBlocking(true);

    console.log('formatos', formatos);

    let fichasEnviadas = 0;
    let evaluacionesEnviadas = 0;
    const errores = [];

    for (let i = 0; i < formatos.length; i++) {
      setBlockingWhatsapp({ id_cuenta: id_cuenta, status: true });
      try {
        const response = await Api.create(`whatsapp/sic_informacion_general/${formatos[i].uuid}/enviar`, {});
        if (response?.data?.success) {
          fichasEnviadas++;
        } else {
          errores.push(response?.data?.error || 'No se pudo enviar la ficha por WhatsApp.');
        }
      } catch (e) {
        errores.push(e?.response?.data?.error || 'No se pudo enviar la ficha por WhatsApp.');
      }
      setBlockingWhatsapp({ id_cuenta: id_cuenta, status: false });
    }

    // El backend responde success:true con enviado:false cuando el productor aún no tiene
    // evaluación de comité en el ciclo; sólo se cuenta cuando realmente se envió.
    setBlockingWhatsapp({ id_cuenta: id_cuenta, status: true });
    try {
      const response = await Api.create(`comite_evaluaciones/${productor_uuid}/${id_ciclo}/whatsapp`, {});
      if (response?.data?.success) {
        if (response.data.enviado) evaluacionesEnviadas++;
      } else {
        errores.push(response?.data?.error || 'No se pudo enviar la evaluación de comité por WhatsApp.');
      }
    } catch (e) {
      errores.push(e?.response?.data?.error || 'No se pudo enviar la evaluación de comité por WhatsApp.');
    }
    setBlockingWhatsapp({ id_cuenta: id_cuenta, status: false });

    whatsappModalRef.current.setBlocking(false);
    whatsappModalRef.current.close();

    const resumen = resumenEnvioWhatsapp(fichasEnviadas, evaluacionesEnviadas);
    if (errores.length === 0) {
      Swal.fire({
        icon: resumen ? 'success' : 'info',
        title: '',
        text: resumen || 'No había fichas ni evaluaciones de comité para enviar.',
      });
    } else {
      const erroresUnicos = [...new Set(errores)].join(' ');
      Swal.fire({
        icon: resumen ? 'warning' : 'error',
        title: '',
        text: resumen ? `${resumen} Sin embargo, hubo problemas: ${erroresUnicos}` : erroresUnicos,
      });
    }
  };

  const resumenEnvioWhatsapp = (fichas, evaluaciones) => {
    const partes = [];
    if (fichas > 0) partes.push(`${fichas} ${fichas === 1 ? 'ficha' : 'fichas'}`);
    if (evaluaciones > 0) partes.push(`${evaluaciones} ${evaluaciones === 1 ? 'evaluación de comité' : 'evaluaciones de comité'}`);
    if (partes.length === 0) return '';
    const verbo = fichas + evaluaciones === 1 ? 'Se envió' : 'Se enviaron';
    return `${verbo} ${partes.join(' y ')} por WhatsApp.`;
  };

  const downloadBlob = async urlFormato => {
    try {
      const response = await Api.download(urlFormato);
      if (response.data.size === 0) {
        setTimeout(() => window.location.reload(), RELOAD_TIME);
        return false;
      }
      const fileName = response.headers['content-disposition'].split('filename=')[1];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 0);
    } catch (e) {
      // console.log(e);
    }
  };

  return (
    <div>
      {/* modals area */}
      <ModalParcelas
        id_cuenta={parcelasSelectedData.id_cuenta}
        nombreProductor={parcelasSelectedData.nombreProductor}
        id_ciclo={ciclo?.id}
        unitConversions={unitConversions}
        dataPadron={data}
        isMielCoop={isMielCoop}
        isCafeCoop={isCafeCoop}
        mostrarAreaCalculada={coopConfig.mostrarAreaCalculada()}
        producto={producto}
        show={showParcelas}
        onClickEdit={onClickEdit}
        onClickEditNumber={onClickEditNumber}
        handleClose={closeModalParcelas}
        updateParentData={setData}
        updateParentDataOriginal={setOriginalData}
        parentRow={selectedRow}
        etiquetas={etiquetas}
      />
      <ModalPadron ref={dialog} />
      <ModalWhatsappConfirm
        ref={whatsappModalRef}
        nombreProductor={whatsappSelected.nombreProductor}
        telefono={whatsappSelected.telefono}
        onConfirm={handleSendWhatsapp}
      />
      <ModalDescargaFichas show={showDescarga} ciclo={ciclo} totalProductores={data.length} onClose={() => setShowDescarga(false)} />
      <ModalPadronCompletoDownload
        isOpen={showDescargaExcel}
        onClose={() => setShowDescargaExcel(false)}
        idCiclo={ciclo?.id}
        usaFormatoApodip={!!+coopConfig.config?.usa_formato_apodip}
        etiquetas={etiquetas}
      />
      {/* header control and filters */}
      <div className="row justify-content-between mb-2">
        <SelectCiclo
          setFilter={evt => {
            setFilter(evt);
          }}></SelectCiclo>
        <div className="col-3 align-self-center">
          {ciclo?.uuid && auth.hasPermission('PADRON_COMPLETO_EXPORTAR') && (
            <div className="d-flex justify-content-end">
              <button type="button" className="btn m-r-2" onClick={() => setShowDescargaExcel(true)}>
                <img src={env.images.icon.xls} className="pr-1" />
                <span style={{ color: '#789D16', fontSize: '0.76rem' }}>Descargar Excel</span>
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Table list */}
      <CardContainer height={'100%'}>
        <div className="row align-items-center">
          <div className="col-10">
            <SearchColumn
              data={originalData}
              columns={searchColumns}
              setDataFilter={evt => {
                setDataFilter(evt);
              }}
            />
          </div>
          <div className="col-2 d-flex justify-content-end">
            {ciclo?.id && auth.hasPermission('PADRON_COMPLETO_EXPORTAR') && (
              <button
                type="button"
                onClick={() => setShowDescarga(true)}
                className="btn btn-sirio"
                style={{ borderRadius: '20px', whiteSpace: 'nowrap', marginBottom: '5px' }}>
                <i className="fa fa-download mr-1"></i> Descargar fichas
              </button>
            )}
          </div>
        </div>

        <NoPagination
          blocking={blocking}
          columns={columns}
          data={data}
          fnBackgroundColor={getBackgroundColor}
          fnColor={getColor}></NoPagination>
      </CardContainer>
    </div>
  );
};

export default PadronCompletoList;
